// @tennis-matches/services/governance
// Parametrisierte App-Zugriffslogik (Katalog-Keys als Input) — Web + shared Admin.

import { isGlobalSuperadminForSession } from "./global-superadmin-session.js";
import {
  ACCESS_CONTROLLED_APP_KEYS_PHASE1,
  HUB_MARKETPLACE_APP_KEYS,
  deriveUserAllowedAppKeysForScope,
  hasActiveTenantRoleBindings,
  normalizeGovernanceAppKey,
  resolveRoleAppAccessEffectiveAtGlobal,
  resolveRoleAppAccessEffectiveAtTenant,
  resolveRoleAppAccessMatrixCell,
} from "./effective-visible-apps.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __toLowerSet(values) {
  const out = new Set();
  const list =
    values instanceof Set
      ? Array.from(values)
      : Array.isArray(values)
        ? values
        : [];
  for (const v of list) {
    const s = __asStr(v).toLowerCase();
    if (s) out.add(s);
  }
  return out;
}

function __normalizeCatalogContext(catalogContext = {}) {
  const accessControlledAppKeys =
    catalogContext.accessControlledAppKeys ?? ACCESS_CONTROLLED_APP_KEYS_PHASE1;
  const marketplaceAppKeys =
    catalogContext.marketplaceAppKeys ?? HUB_MARKETPLACE_APP_KEYS;
  const accessSet = __toLowerSet(accessControlledAppKeys);
  const marketplaceSet = __toLowerSet(marketplaceAppKeys);
  return { accessControlledAppKeys, accessSet, marketplaceSet };
}

function __rolesAtTenantScope(roleBindings, tenantScopeId) {
  const sid = __asStr(tenantScopeId);
  const roles = new Set();
  for (const b of Array.isArray(roleBindings) ? roleBindings : []) {
    if (__asStr(b?.scope).toLowerCase() !== "tenant") continue;
    if (__asStr(b?.scope_id) !== sid) continue;
    if (b?.is_active === false) continue;
    const role = __asStr(b?.role).toLowerCase();
    if (role) roles.add(role);
  }
  return roles;
}

function __fallbackAppsForRoles(roles, accessControlledAppKeys) {
  const rset =
    roles instanceof Set ? roles : new Set(Array.isArray(roles) ? roles : []);
  const adminRoles = new Set(["admin", "vereinsadmin", "vorstand", "owner"]);
  if (Array.from(rset).some((r) => adminRoles.has(r))) {
    return {
      ok: true,
      allApps: true,
      allowedAppKeys: new Set(accessControlledAppKeys),
    };
  }
  const allowed = new Set();
  if (
    rset.has("trainer") ||
    rset.has("trainer_coordinator") ||
    rset.has("jugendwart")
  ) {
    allowed.add("trainerstunden");
    allowed.add("trainer");
  }
  if (rset.has("kassenwart")) {
    allowed.add("clubhub");
  }
  if (rset.has("sportwart")) {
    allowed.add("matchmixer");
    allowed.add("clubmeisterschaft");
    allowed.add("medenspiele");
  }
  if (rset.has("team_captain")) {
    allowed.add("medenspiele");
  }
  if (rset.has("parent") || rset.has("player")) {
    allowed.add("trainerstunden");
  }
  return { ok: true, allApps: false, allowedAppKeys: allowed };
}

export function deriveAllowedAppKeysFromRoleBindings(
  roleBindings,
  authUser = null,
  catalogContext = {},
) {
  const { accessControlledAppKeys } = __normalizeCatalogContext(catalogContext);
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const roles = new Set(
    list.map((b) => __asStr(b?.role).toLowerCase()).filter(Boolean),
  );

  if (isGlobalSuperadminForSession(list, authUser)) {
    return {
      ok: true,
      allApps: true,
      roles,
      allowedAppKeys: new Set(accessControlledAppKeys),
    };
  }

  const adminRoles = new Set(["admin", "vereinsadmin", "vorstand", "owner"]);
  if (Array.from(roles).some((r) => adminRoles.has(r))) {
    return {
      ok: true,
      allApps: true,
      roles,
      allowedAppKeys: new Set(accessControlledAppKeys),
    };
  }

  const allowed = new Set();
  if (
    roles.has("trainer") ||
    roles.has("trainer_coordinator") ||
    roles.has("jugendwart")
  ) {
    allowed.add("trainerstunden");
    allowed.add("trainer");
  }
  if (roles.has("kassenwart")) {
    allowed.add("clubhub");
  }
  if (roles.has("sportwart")) {
    allowed.add("matchmixer");
    allowed.add("clubmeisterschaft");
    allowed.add("medenspiele");
  }
  if (roles.has("team_captain")) {
    allowed.add("medenspiele");
  }
  if (roles.has("parent") || roles.has("player")) {
    allowed.add("trainerstunden");
  }

  return { ok: true, allApps: false, roles, allowedAppKeys: allowed };
}

export function deriveGovernedMarketplaceAppKeys(
  roleBindings,
  roleAppAccessRows,
  authUser = null,
  catalogContext = {},
) {
  const { marketplaceSet: marketplace } =
    __normalizeCatalogContext(catalogContext);
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const allowed = new Set();

  const byTenant = new Map();
  for (const b of list) {
    const scope = __asStr(b?.scope).toLowerCase();
    const scopeId = __asStr(b?.scope_id);
    const role = __asStr(b?.role).toLowerCase();
    if (scope !== "tenant" || !scopeId || !role) continue;
    const prev = byTenant.get(scopeId) || new Set();
    prev.add(role);
    byTenant.set(scopeId, prev);
  }

  for (const [scopeId, roles] of byTenant) {
    for (const role of roles) {
      for (const appKey of marketplace) {
        if (
          resolveRoleAppAccessEffectiveAtTenant(role, appKey, scopeId, rows)
        ) {
          allowed.add(appKey);
        }
      }
    }
  }

  const globalRoles = new Set();
  for (const b of list) {
    const scope = __asStr(b?.scope).toLowerCase();
    const scopeId = __asStr(b?.scope_id);
    const role = __asStr(b?.role).toLowerCase();
    if (scope === "global" && scopeId === "global" && role)
      globalRoles.add(role);
  }
  if (isGlobalSuperadminForSession(list, authUser)) {
    globalRoles.add("superadmin");
  }
  for (const role of globalRoles) {
    for (const appKey of marketplace) {
      if (resolveRoleAppAccessEffectiveAtGlobal(role, appKey, rows)) {
        allowed.add(appKey);
      }
    }
  }

  return allowed;
}

export function deriveEffectiveMarketplaceAppsForMemberRoles(
  memberRoles,
  tenantScopeId,
  roleAppAccessRows,
  catalogContext = {},
) {
  const { marketplaceSet: marketplace } =
    __normalizeCatalogContext(catalogContext);
  const sid = __asStr(tenantScopeId);
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const allowed = new Set();
  if (!sid) return allowed;

  const roles = new Set();
  for (const r of memberRoles instanceof Set
    ? memberRoles
    : Array.isArray(memberRoles)
      ? memberRoles
      : []) {
    const role = __asStr(r).toLowerCase();
    if (role) roles.add(role);
  }

  for (const role of roles) {
    for (const appKey of marketplace) {
      if (resolveRoleAppAccessEffectiveAtTenant(role, appKey, sid, rows)) {
        allowed.add(appKey);
      }
    }
  }
  return allowed;
}

export function deriveEffectiveMemberMarketplaceApps(
  memberRoles,
  tenantScopeId,
  roleAppAccessRows,
  userEntitlements = [],
  catalogContext = {},
) {
  const matrix = deriveEffectiveMarketplaceAppsForMemberRoles(
    memberRoles,
    tenantScopeId,
    roleAppAccessRows,
    catalogContext,
  );
  const denied = new Set();
  const explicitGrant = new Set();
  const sid = __asStr(tenantScopeId);

  for (const e of Array.isArray(userEntitlements) ? userEntitlements : []) {
    if (__asStr(e?.scope).toLowerCase() !== "tenant") continue;
    if (__asStr(e?.scope_id) !== sid) continue;
    const app = normalizeGovernanceAppKey(e?.app_key);
    if (!app) continue;
    if (e?.enabled === false) denied.add(app);
    else explicitGrant.add(app);
  }

  const out = new Set();
  for (const k of matrix) {
    if (!denied.has(k)) out.add(k);
  }
  for (const k of explicitGrant) {
    if (!denied.has(k)) out.add(k);
  }
  return out;
}

function __deriveNonMarketplaceAppKeysFromBindings(
  roleBindings,
  authUser = null,
  catalogContext = {},
) {
  const { accessControlledAppKeys, marketplaceSet: marketplace } =
    __normalizeCatalogContext(catalogContext);
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const roles = new Set(
    list.map((b) => __asStr(b?.role).toLowerCase()).filter(Boolean),
  );
  const allowed = new Set();

  const adminRoles = new Set(["admin", "vereinsadmin", "vorstand", "owner"]);
  if (Array.from(roles).some((r) => adminRoles.has(r))) {
    for (const k of accessControlledAppKeys) {
      if (!marketplace.has(normalizeGovernanceAppKey(k)))
        allowed.add(normalizeGovernanceAppKey(k));
    }
    return allowed;
  }

  const fb = __fallbackAppsForRoles(roles, accessControlledAppKeys);
  for (const k of fb.allowedAppKeys || []) {
    if (!marketplace.has(normalizeGovernanceAppKey(k)))
      allowed.add(normalizeGovernanceAppKey(k));
  }
  return allowed;
}

export function deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccess(
  roleBindings,
  roleAppAccessRows,
  authUser = null,
  catalogContext = {},
) {
  const { accessControlledAppKeys } = __normalizeCatalogContext(catalogContext);
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];

  const marketplace = deriveGovernedMarketplaceAppKeys(
    list,
    rows,
    authUser,
    catalogContext,
  );
  const allowed = new Set(marketplace);

  for (const k of __deriveNonMarketplaceAppKeysFromBindings(
    list,
    authUser,
    catalogContext,
  )) {
    allowed.add(k);
  }

  if (allowed.size === 0 && hasActiveTenantRoleBindings(list)) {
    const roles = new Set();
    for (const b of list) {
      if (__asStr(b?.scope).toLowerCase() !== "tenant") continue;
      if (b?.is_active === false) continue;
      const role = __asStr(b?.role).toLowerCase();
      if (role) roles.add(role);
    }
    const fb = __fallbackAppsForRoles(roles, accessControlledAppKeys);
    for (const appKey of fb.allowedAppKeys || []) allowed.add(appKey);
  }

  return { ok: true, allApps: false, allowedAppKeys: allowed };
}

export function shouldShowInTenantRoleMatrix(
  role,
  appKey,
  tenantScopeId,
  globalScopeAppKeys,
  rows,
) {
  const app = normalizeGovernanceAppKey(appKey);
  const globalKeys =
    globalScopeAppKeys instanceof Set
      ? globalScopeAppKeys
      : new Set(
          (Array.isArray(globalScopeAppKeys) ? globalScopeAppKeys : []).map(
            (k) => normalizeGovernanceAppKey(k),
          ),
        );
  if (!globalKeys.has(app)) return false;

  const globalCell = resolveRoleAppAccessMatrixCell(
    role,
    appKey,
    "global",
    "global",
    rows,
  );
  if (!globalCell.checked) return false;

  void tenantScopeId;
  return true;
}

export function deriveAllowedAppKeysFromBindingsAndEntitlements(
  roleBindings,
  entitlements,
  authUser = null,
  catalogContext = {},
) {
  const base = deriveAllowedAppKeysFromRoleBindings(
    roleBindings,
    authUser,
    catalogContext,
  );
  if (!base?.ok)
    return { ok: false, allApps: false, allowedAppKeys: new Set() };
  if (base.allApps) return base;

  const allowed = new Set(base.allowedAppKeys || []);
  const list = Array.isArray(entitlements) ? entitlements : [];
  for (const e of list) {
    const app = __asStr(e?.app_key).toLowerCase();
    if (app) allowed.add(normalizeGovernanceAppKey(app));
  }
  return { ...base, allowedAppKeys: allowed };
}

export function deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess(
  roleBindings,
  entitlements,
  roleAppAccessRows,
  authUser = null,
  catalogContext = {},
) {
  const { marketplaceSet: marketplace } =
    __normalizeCatalogContext(catalogContext);
  const base = deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccess(
    roleBindings,
    roleAppAccessRows,
    authUser,
    catalogContext,
  );
  if (!base?.ok)
    return { ok: false, allApps: false, allowedAppKeys: new Set() };
  if (base.allApps) return base;
  const allowed = new Set(base.allowedAppKeys || []);
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  const rows = Array.isArray(roleAppAccessRows) ? roleAppAccessRows : [];
  const userId = __asStr(authUser?.id);
  const deniedMarketplace = new Set();

  for (const e of Array.isArray(entitlements) ? entitlements : []) {
    const app = normalizeGovernanceAppKey(e?.app_key);
    if (!app) continue;
    if (!marketplace.has(app)) {
      allowed.add(app);
      continue;
    }
    if (userId && __asStr(e?.user_id) !== userId) continue;
    const scope = __asStr(e?.scope).toLowerCase();
    const scopeId = __asStr(e?.scope_id);
    if (scope !== "tenant" || !scopeId) continue;
    if (e?.enabled === false) {
      deniedMarketplace.add(app);
      continue;
    }
    const rolesAtScope = __rolesAtTenantScope(list, scopeId);
    const globalOk = Array.from(rolesAtScope).some(
      (role) =>
        resolveRoleAppAccessMatrixCell(role, app, "global", "global", rows)
          .checked,
    );
    if (globalOk) allowed.add(app);
  }
  for (const app of deniedMarketplace) {
    allowed.delete(app);
  }
  return { ...base, allowedAppKeys: allowed };
}

export function deriveScopeAvailableAppKeys({
  scope = "tenant",
  scopeId,
  appManifests,
  isLoaded = false,
  catalogContext = {},
  extraGlobalScopeAppKeys = [],
} = {}) {
  const { accessSet } = __normalizeCatalogContext(catalogContext);
  const normalizedScope = __asStr(scope).toLowerCase();
  const normalizedScopeId = __asStr(scopeId);
  const manifestsLoaded = Boolean(isLoaded);

  if (!manifestsLoaded || !Array.isArray(appManifests)) {
    return {
      ok: false,
      loaded: false,
      reason: "scope-availability-not-loaded",
      availableAppKeys: new Set(),
    };
  }

  const available = new Set();
  for (const row of appManifests) {
    const appKey = normalizeGovernanceAppKey(row?.id || row?.app_id);
    if (!appKey || !accessSet.has(appKey)) continue;
    const tenantId = __asStr(row?.tenantId || row?.tenant_id);
    const isGlobalManifest = !tenantId;

    if (normalizedScope === "global") {
      if (isGlobalManifest) available.add(appKey);
      continue;
    }

    if (
      isGlobalManifest ||
      (normalizedScopeId && tenantId === normalizedScopeId)
    ) {
      available.add(appKey);
    }
  }

  if (normalizedScope === "global") {
    const { marketplaceAppKeys } = __normalizeCatalogContext(catalogContext);
    const extras =
      Array.isArray(extraGlobalScopeAppKeys) &&
      extraGlobalScopeAppKeys.length > 0
        ? extraGlobalScopeAppKeys
        : marketplaceAppKeys;
    for (const appKey of extras) {
      const k = normalizeGovernanceAppKey(appKey);
      if (k && accessSet.has(k)) available.add(k);
    }
  }

  return {
    ok: true,
    loaded: true,
    scope: normalizedScope || "tenant",
    scopeId: normalizedScopeId,
    availableAppKeys: available,
  };
}

export function getEffectiveVisibleAppsForScope({
  scope = "tenant",
  scopeId,
  appManifests,
  isManifestsLoaded = false,
  roleBindings,
  entitlements,
  roleAppAccessRows,
  isFeatureEnabled = null,
  authUser = null,
  catalogContext = {},
  extraGlobalScopeAppKeys = [],
} = {}) {
  const { accessControlledAppKeys, marketplaceSet } =
    __normalizeCatalogContext(catalogContext);
  const catalogKeys = __toLowerSet(accessControlledAppKeys);
  const availability = deriveScopeAvailableAppKeys({
    scope,
    scopeId,
    appManifests,
    isLoaded: isManifestsLoaded,
    catalogContext,
    extraGlobalScopeAppKeys,
  });

  if (availability?.loaded !== true) {
    return {
      ok: false,
      loaded: false,
      reason: "scope-availability-not-loaded",
      effectiveVisibleAppKeys: new Set(),
      globallyAllowedAppKeys: new Set(),
      userAllowedAppKeys: new Set(),
      allApps: false,
    };
  }

  const scopeAvailable = __toLowerSet(availability?.availableAppKeys);
  const featureEnabled = new Set();
  for (const appKey of catalogKeys) {
    const enabled =
      typeof isFeatureEnabled === "function"
        ? isFeatureEnabled(appKey) !== false
        : true;
    if (enabled) featureEnabled.add(appKey);
  }

  const globallyAllowed = new Set();
  for (const appKey of catalogKeys) {
    if (scopeAvailable.has(appKey) && featureEnabled.has(appKey))
      globallyAllowed.add(appKey);
  }

  const userAllowedRaw = __toLowerSet(
    deriveUserAllowedAppKeysForScope({
      scope,
      scopeId,
      roleBindings,
      entitlements,
      roleAppAccessRows,
      authUser,
      marketplaceAppKeys: marketplaceSet,
      accessControlledAppKeys,
    }),
  );

  const effective = new Set();
  for (const appKey of userAllowedRaw) {
    if (globallyAllowed.has(appKey)) effective.add(appKey);
  }

  return {
    ok: true,
    loaded: true,
    allApps: false,
    globallyAllowedAppKeys: globallyAllowed,
    userAllowedAppKeys: userAllowedRaw,
    effectiveVisibleAppKeys: effective,
  };
}

export function canAccessAccessRequestsAdmin(roleBindings, authUser = null) {
  const list = Array.isArray(roleBindings) ? roleBindings : [];
  if (isGlobalSuperadminForSession(list, authUser)) return true;

  const allow = new Set(["admin", "owner", "vereinsadmin", "vorstand"]);
  return list.some((b) => {
    const role = __asStr(b?.role).toLowerCase();
    const scope = __asStr(b?.scope).toLowerCase();
    return scope === "tenant" && allow.has(role);
  });
}

export function canAccessApp(
  appKey,
  roleBindings,
  authUser = null,
  catalogContext = {},
) {
  const app = normalizeGovernanceAppKey(appKey);
  if (!app) return false;
  const res = deriveAllowedAppKeysFromRoleBindings(
    roleBindings,
    authUser,
    catalogContext,
  );
  if (!res?.ok) return false;
  if (res.allApps) return true;
  return res.allowedAppKeys.has(app);
}

export function canAccessAppWithEntitlements(
  appKey,
  roleBindings,
  entitlements,
  authUser = null,
  catalogContext = {},
) {
  const app = normalizeGovernanceAppKey(appKey);
  if (!app) return false;
  const res = deriveAllowedAppKeysFromBindingsAndEntitlements(
    roleBindings,
    entitlements,
    authUser,
    catalogContext,
  );
  if (!res?.ok) return false;
  if (res.allApps) return true;
  return res.allowedAppKeys.has(app);
}

export function canAccessAppWithEntitlementsAndRoleAppAccess(
  appKey,
  roleBindings,
  entitlements,
  roleAppAccessRows,
  authUser = null,
  catalogContext = {},
) {
  const app = normalizeGovernanceAppKey(appKey);
  if (!app) return false;
  const res = deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess(
    roleBindings,
    entitlements,
    roleAppAccessRows,
    authUser,
    catalogContext,
  );
  if (!res?.ok) return false;
  if (res.allApps) return true;
  return res.allowedAppKeys.has(app);
}
